import { createFileRoute } from '@tanstack/react-router';
import { Plus } from '@osd/icons/plus';
import { Folder } from '@osd/icons/folder';
import { Banner } from '@osd/ui/banner';
import { Button } from '@osd/ui/button';
import { EmptyState } from '@osd/ui/empty-state';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/empty-state — live docs for the `EmptyState` composite.
 *
 * EmptyState is a centered zero-data placeholder: optional icon,
 * title, description, primary action, and secondary action. Use it
 * for empty lists, no-search-results, no-permissions, and similar
 * "there's nothing here yet" states.
 */
export const Route = createFileRoute('/ui/_layout/empty-state')({
  component: EmptyStatePage,
});

function EmptyStatePage() {
  return (
    <Stack gap={6}>
      <Section
        title="EmptyState"
        hint="Centered zero-data placeholder — icon + title + description + (primary, secondary) action"
      >
        <Text>
          The most prominent content is the description: it should answer &ldquo;what&rsquo;s
          missing / what do I do next&rdquo;. Always pair with a single primary action so the user
          has a clear next step.
        </Text>
      </Section>

      <Section title="Minimal" hint="Description only">
        <EmptyState description="No items match your filters." />
      </Section>

      <Section title="Icon + title + description" hint="Common shape">
        <EmptyState
          icon={<PlusIcon />}
          title="No projects yet"
          description="Create your first project to get started."
        />
      </Section>

      <Section title="With primary + secondary actions" hint="The workhorse variant">
        <EmptyState
          icon={<FolderIcon />}
          title="Your watchlist is empty"
          description="Track issues, merge requests, and epics by starring them."
          action={<Button>Browse projects</Button>}
          secondaryAction={<a href="/docs/watchlist">Learn more</a>}
        />
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Full EmptyState"
            code={`<EmptyState
  icon={<FolderIcon />}
  title="No projects yet"
  description="Create your first project to get started."
  action={<Button onClick={openCreate}>New project</Button>}
  secondaryAction={<a href="/docs">Learn more</a>}
/>`}
          />
          <CodeBlock
            language="tsx"
            title="Inside a card"
            code={`<Card>
  <Card.Title>Recent activity</Card.Title>
  <Card.Body>
    <EmptyState
      description="Nothing has happened in the last 7 days."
      action={<Button>Refresh</Button>}
    />
  </Card.Body>
</Card>`}
          />
        </Stack>
      </Section>

      <Banner variant="info" title="Accessibility">
        EmptyState renders with <code>role="status"</code> so assistive tech announces the empty
        state when the page loads. Avoid action labels that duplicate the title — they read as
        noise.
      </Banner>
    </Stack>
  );
}

const PlusIcon = Plus;
const FolderIcon = Folder;
